/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import type { Solid, Pattern } from '@web-stories-wp/patterns';
import type {
  Resource,
  SequenceResource,
  GifResource,
  VideoResource,
} from '@web-stories-wp/media';
import type stickers from '@web-stories-wp/stickers';

/**
 * Internal dependencies
 */
import type { ElementType } from './elementType';
import type { Track } from './media';
import type { GoogleFontData, SystemFontData, CustomFontData } from './data';

// For some reason importing ElementBox from @web-stories-wp/units doesn't work in some files
// (`duplicateElement` throws an error because it thinks the element doesn't have a x or y property)
// If updating, please also update web-stories-wp/units/src/types.ts
interface ElementBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotationAngle: number;
}

export enum LinkType {
  Regular = 'regular',
  Branching = 'branching',
}

export interface Link {
  type?: LinkType;
  pageId?: ElementId;
  url: string;
  desc?: string;
  needsProxy?: boolean;
  icon?: string | null;
  rel?: string[];
}

export interface Flip {
  vertical: boolean;
  horizontal: boolean;
}

export interface Mask {
  type: string; // 'rectangle', etc
  // TODO(#12259): Remove these from the type & from templates.
  showInLibrary?: boolean;
  name?: string;
  path?: string;
  ratio?: number;
  iconPath?: string;
  iconRatio?: number;
  supportsBorder?: boolean;
}

export interface Border {
  top: number;
  right: number;
  bottom: number;
  left: number;
  locked?: boolean;
  lockedWidth?: boolean;
  color?: Solid;
}

export interface BorderRadius {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
  locked: boolean;
}

export type ElementId = string;

export type BlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'linear-light'
  | 'hard-light'
  | 'soft-light'
  | 'difference'
  | 'exclusion';

export type TextEffectBlendMode =
  | Exclude<BlendMode, 'linear-light'>
  | 'linear-burn';

export interface BaseElement extends ElementBox {
  id: ElementId;
  type: ElementType;
  textBoxAttachment?: TextBoxAttachment;
  mask?: Mask;
  link?: Link;
  blendMode?: BlendMode;
  opacity?: number;
  lockAspectRatio?: boolean;
  flip?: Flip;
  groupId?: string;
  border?: Border;
  borderRadius?: BorderRadius;
  layerName?: string;
  isLocked?: boolean;
  isHidden?: boolean;
}

export interface LinkableElement extends BaseElement {
  link: Link;
}

export interface DefaultBackgroundElement extends BaseElement {
  isDefaultBackground: boolean;
  backgroundColor: Solid;
}

export interface BackgroundableElement extends BaseElement {
  isBackground?: boolean;
}

export interface MediaElement extends BackgroundableElement {
  resource: Resource;
  scale?: number;
  focalX?: number;
  focalY?: number;
  alt?: string;
}

export interface SequenceMediaElement extends MediaElement {
  resource: SequenceResource;
}

export interface DynamicCropPosition {
  timestamp: number;
  xPositionPercent: number;
  y?: number;
  isPausedMovement?: boolean;
  isUserEdited?: boolean;
}

export interface DynamicCrop {
  aspectRatio: string;
  height?: number;
  y?: number;
  positions: DynamicCropPosition[];
}

export interface VideoElement extends SequenceMediaElement {
  type: ElementType.Video;
  tracks: Track[];
  resource: VideoResource;
  poster?: string;
  loop?: boolean;
  volume: number;
  dynamicCrop?: DynamicCrop;
}

export interface GifElement extends SequenceMediaElement {
  type: ElementType.Gif;
  poster?: string;
  resource: GifResource;
}

export interface ImageElement extends MediaElement {
  backdropBlur?: number;
  backdropEdgeSoftness?: number;
  type: ElementType.Image;
  alt?: string;
  lutFilter?: {
    versionId: string;
    intensity: number;
  };
}

export interface OverlayableElement extends BaseElement {
  overlay?: Pattern | null;
  overlays?: {
    id: string;
    pattern: Pattern;
    isHidden?: boolean;
    blur?: number;
  }[];
}

export interface StickerElement extends BaseElement {
  type: ElementType.Sticker;
  sticker: {
    type: keyof typeof stickers;
  };
}

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonObject
  | JsonValue[];

export interface JsonObject {
  [key: string]: unknown;
}

export interface LottieDefinition extends JsonObject {
  v: string;
  w: number;
  h: number;
  fr: number;
  ip?: number;
  op: number;
  layers: unknown[];
}

export type LottieParameterType = 'text' | 'number' | 'color';

export type LottieParameterBinding =
  | {
      kind: 'slot';
      slotId: string;
      slotType?: string;
    }
  | {
      kind: 'jsonPointer';
      jsonPointer: string;
    };

export interface LottieParameter {
  key: string;
  label?: string;
  type: LottieParameterType;
  required?: boolean;
  constraints?: {
    maxLength?: number;
    min?: number;
    max?: number;
  };
  bindings: LottieParameterBinding[];
}

export interface LottieParameterSchema {
  version: 1;
  parameters: LottieParameter[];
}

export type LottieColorArray =
  | [number, number, number]
  | [number, number, number, number];

export type LottieParameterValue = string | number | LottieColorArray;

export type LottieParameterValues = Record<string, LottieParameterValue>;

export type LottiePlaybackEndBehavior = 'freeze' | 'reset' | 'hide';

export interface LottiePlayback {
  autoplay: boolean;
  loop: boolean;
  loopCount?: number;
  startDelay: number;
  holdDuration?: number;
  speed: number;
  endBehavior: LottiePlaybackEndBehavior;
}

export type LottieFontFormat = 'woff' | 'woff2' | 'truetype' | 'opentype';

export interface LottieFont {
  family: string;
  url: string;
  format: LottieFontFormat;
  weight: number | 'normal' | 'bold';
  style: 'normal' | 'italic';
}

export interface LottieElement extends BaseElement {
  type: ElementType.Lottie;
  lottieContractVersion: 1;
  animationId: string;
  title: string;
  definition: LottieDefinition;
  intrinsicWidth: number;
  intrinsicHeight: number;
  fps: number;
  duration: number;
  parameterSchema?: LottieParameterSchema;
  defaultParameterValues?: LottieParameterValues;
  parameterValues?: LottieParameterValues;
  fonts: LottieFont[];
  playback: LottiePlayback;
  previewImageUrl?: string | null;
}

export type TextElementFont = GoogleFontData | SystemFontData | CustomFontData;

export type TextFontReferenceIdentity =
  | {
      kind: 'asset';
      sourceId: string;
    }
  | {
      kind: 'metadata';
      fingerprint: string;
    };

export type TextFontReferenceV1 = {
  id: string;
  identity: TextFontReferenceIdentity;
  revision: string;
  font: TextElementFont;
};

export type TextFontReferencesV1 = {
  version: 1;
  items: TextFontReferenceV1[];
};

export interface Padding {
  horizontal: number;
  vertical: number;
  locked: boolean;
  hasHiddenPadding?: boolean;
}

export enum TextBorderAlignment {
  Center = 'center',
  Inside = 'inside',
  Outside = 'outside',
}

// There are more values in the SVG spec, but for now we only support the same values as Figma
// (since that's what the design team uses)
export enum TextBorderLineJoin {
  Miter = 'miter',
  Bevel = 'bevel',
  Round = 'round',
}

export enum TextBorderLineCap {
  Butt = 'butt',
  Round = 'round',
  Square = 'square',
}

export enum TextBorderStyleType {
  Solid = 'solid',
  Dashed = 'dashed',
}

export type SolidTextBorderStyle = {
  type: TextBorderStyleType.Solid;
};

export type DashedTextBorderStyle = {
  type: TextBorderStyleType.Dashed;
  dashLength: number;
  gapLength: number;
};

export type TextBorderStyle = SolidTextBorderStyle | DashedTextBorderStyle;

export type TextBorder = {
  alignment: TextBorderAlignment;
  lineCap: TextBorderLineCap;
  lineJoin: TextBorderLineJoin;
  miterLimit: number;
  style: TextBorderStyle;
  width: number;
  color: Solid | Pattern;
};

export type TextAlign = 'center' | 'justify' | 'left' | 'right';

export type TextBoxAttachmentAlignment = 'start' | 'center' | 'end';

export type TextBoxAttachmentPosition = 'above' | 'below';

interface TextBoxAttachmentOffsets {
  targetId: ElementId;
  alignment: TextBoxAttachmentAlignment;
  gap: number;
  crossAxisOffset: number;
  coordinateSpace: 'target-local';
}

// Versions 1/2 keep the original above-target relationship. Version 3 follows
// the target bottom; a positive gap separates the chosen edges in either mode.
export type TextBoxAttachment = TextBoxAttachmentOffsets &
  (
    | { version: 1 | 2; targetEdge: 'top'; ownEdge: 'bottom' }
    | { version: 3; targetEdge: 'bottom'; ownEdge: 'top' }
  );

export type TextInnerGlow = {
  version: 1;
  enabled: boolean;
  color: string;
  opacity: number;
  size: number;
  choke: number;
  range: number;
  blendMode: TextEffectBlendMode;
  technique: 'softer';
  source: 'edge' | 'center';
  contour: 'linear' | 'smooth' | 'ring';
  noise: 0;
  jitter: 0;
  antiAlias: false;
};

export type TextDepthV1 = {
  version: 1;
  mode: 'off' | 'raised' | 'recessed';
  size: number;
  lightDirection: number;
  softness: number;
  strength: number;
};

export type TextBevelDepth = {
  version: 2;
  mode: 'off' | 'raised' | 'recessed';
  style: 'inner-bevel';
  technique: 'chisel-hard' | 'smooth';
  size: number;
  depth: number;
  softness: number;
  lightDirection: number;
  altitude: number;
  highlight: { color: string; opacity: number; blendMode: TextEffectBlendMode };
  shadow: { color: string; opacity: number; blendMode: TextEffectBlendMode };
  glossContour: 'linear' | 'rounded' | 'ring';
  contour: { enabled: boolean; profile: 'linear' | 'ring'; range: number };
};

export type TextDepth = TextDepthV1 | TextBevelDepth;

export type TextTexture = {
  src: string;
  width?: number;
  height?: number;
  scale?: number;
  fallbackColor?: Solid;
  sizing?: 'cover' | 'contain' | 'stretch';
  position?: {
    x: number;
    y: number;
  };
};

export type TextListMarkerPreset = 'disc' | 'circle' | 'square' | 'custom';

export type TextListStyleV1 = {
  version: 1;
  marker: {
    preset: TextListMarkerPreset;
    glyph?: string;
    color?: Solid;
    size?: number;
    /** Captured tenant ratio, removed by an explicit size override. */
    sizeRatio?: number;
    baselineShift?: number;
    horizontalShift?: number;
  };
  indent: number;
  nestedIndent: number;
  markerGap: number;
  itemSpacing: number;
};

export interface TextElement extends BaseElement {
  backgroundColor: Pattern;
  color?: Pattern;
  textTexture?: TextTexture;
  textDepth?: TextDepth;
  textInnerGlow?: TextInnerGlow;
  content: string;
  font: TextElementFont;
  fontSize: number;
  fontReferences?: TextFontReferencesV1;
  listStyle?: TextListStyleV1;

  backgroundTextMode?: string;
  textBoxBackdropBlur?: number;
  textBoxBackdropEdgeSoftness?: number;
  tagName?: 'h1' | 'h2' | 'h3' | 'p' | 'auto';
  padding?: Padding;
  marginOffset: number;
  marginOffsetTop?: number;
  marginOffsetBottom?: number;
  lineHeight: number;
  textAlign: TextAlign;

  type: ElementType.Text;
  fontStyle?: 'italic' | 'normal';
  fontWeight?: number;

  textShadowStyle?: 'NONE' | 'DROP_SHADOW';
  textShadowX?: number;
  textShadowY?: number;
  textShadowBlur?: number;
  textShadowColor?: Solid;
  shadowTarget?: 'text' | 'textBox';

  textBorder?: TextBorder;

  lastUpdatedBy?: 'suggestion' | 'user';

  verticalTrim?: 'none' | 'baseline';
  verticalAnchor?: 'top' | 'center' | 'bottom';
}

export interface ShapeElement extends BaseElement {
  backdropBlur?: number;
  backdropEdgeSoftness?: number;
  type: ElementType.Shape;
  backgroundColor?: Pattern;
  isBackground?: boolean;
  isDefaultBackground?: boolean;
}

export type Element =
  | BackgroundableElement
  | DefaultBackgroundElement
  | GifElement
  | ImageElement
  | ShapeElement
  | LinkableElement
  | LottieElement
  | MediaElement
  | OverlayableElement
  | SequenceMediaElement
  | StickerElement
  | TextElement
  | VideoElement;
